import {
	FormItem,
	Toggle,
	ValidationError
} from "client/Components";
import { IObjectInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useCallback } from "react";
import { AuthorsFormModel } from "./EditCommunityDialog";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import {
	UserAndUserGroupCollection,
	UserAndUserGroupCollectionValue,
	UserAndUserGroupKind
} from "client/Features/Users/UserAndUserGroupCollection/UserAndUserGroupCollection";
import { ActiveTab } from "client/Features/Users/UserAndUserGroupTabSelector/UserAndUserGroupTabSelector";
import { PermissionSetModel } from "client/Api/common";
import { CommunityVisibility } from "client/Api/Communities/community";

export type AuthorsProps = {
	attachable: IValueInputAttachable<AuthorsFormModel>;
	visibility: CommunityVisibility;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
	onAfterAuthorsChanged: (
		oldValue: PermissionSetModel,
		newValue: PermissionSetModel
	) => void;
};

export const Authors = (props: AuthorsProps): ReactElement => {
	const {
		attachable,
		visibility,
		usersAndUserGroupsData,
		onAfterAuthorsChanged
	} = props;

	const showUserAndUserGroupCollection: boolean = !attachable.value
		.everyoneWhoHasAccess;

	const specificSetAttachable: IObjectInputAttachable<PermissionSetModel> = attachable.for("specificSet");

	const authorsChangeHandler = useCallback(
		(newValue: UserAndUserGroupCollectionValue): void => {
			specificSetAttachable.reducer
				.setValue({
					...newValue
				});

			specificSetAttachable.validation
				.triggerInputFinished();

			onAfterAuthorsChanged(
				specificSetAttachable.value,
				newValue
			);
		},
		[specificSetAttachable, onAfterAuthorsChanged]
	);

	return (
		<>
			<FormItem>
				<Toggle
					{...attachable.for("everyoneWhoHasAccess").attachToCheckbox()}
					text={localize(visibilityToEveryoneCanPostKey[visibility])}
				/>

				<ValidationError
					{...attachable.for("everyoneWhoHasAccess").attachToValidationError()}
				/>
			</FormItem>
			{showUserAndUserGroupCollection && (
				<FormItem height="stretch">
					<UserAndUserGroupCollection
						activeTab={ActiveTab.UserGroup}
						value={specificSetAttachable.value}
						mode={{
							kind: UserAndUserGroupKind.Edit,
							dialogTitleWhenAddingUsersAndUserGroups: localize("_AddGroupsOrUsers"),
							onChange: authorsChangeHandler
						}}
						allUsersAndUserGroupsData={usersAndUserGroupsData}
					/>

					<ValidationError
						{...specificSetAttachable.attachToValidationError()}
					/>
				</FormItem>
			)}
		</>
	);
};
Authors.displayName = "Authors";

const visibilityToEveryoneCanPostKey: Record<CommunityVisibility, LocalizationKey> = {
	[CommunityVisibility.Public]: "_EveryoneCanPost",
	[CommunityVisibility.Private]: "_EveryoneWithAccessCanPost",
	[CommunityVisibility.Hidden]: "_EveryoneWithAccessCanPost"
};